/**
 * The words people see for things the data model names differently.
 *
 * The record keeps five statuses and thirty-one categories because the audit
 * trail needs them. The screens show four states and no category picker; this
 * file is the one place that translation lives.
 */
import type { Expense } from './types.js';

export const STATUS_LABEL: Record<Expense['status'], string> = {
  draft: 'To review',
  verified: 'Ready to claim',
  reimbursed: 'Reimbursed',
  // Two statuses, one meaning to the person: this is not being claimed. The
  // record's history says which, and why.
  excluded: 'Not claiming',
  void: 'Not claiming',
};

export type StatusFilter = 'all' | 'draft' | 'verified' | 'reimbursed' | 'not_claiming';

export const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'To review' },
  { id: 'verified', label: 'Ready to claim' },
  { id: 'reimbursed', label: 'Reimbursed' },
  { id: 'not_claiming', label: 'Not claiming' },
];

export function matchesFilter(e: Expense, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'not_claiming') return e.status === 'excluded' || e.status === 'void';
  return e.status === filter;
}

/** Kept for "More details" on the edit form; never required. */
export const CATEGORIES: { id: string; label: string }[] = [
  { id: 'medical_services', label: 'Doctor / medical services' },
  { id: 'dental', label: 'Dental' },
  { id: 'vision', label: 'Vision' },
  { id: 'hospital', label: 'Hospital / facility' },
  { id: 'lab_imaging', label: 'Lab and imaging' },
  { id: 'prescription_drugs', label: 'Prescription drugs' },
  { id: 'otc_drugs', label: 'Over-the-counter medicine' },
  { id: 'menstrual_care', label: 'Menstrual care products' },
  { id: 'medical_equipment', label: 'Medical equipment and supplies' },
  { id: 'mental_health', label: 'Mental health / therapy' },
  { id: 'chiropractic', label: 'Chiropractic' },
  { id: 'physical_therapy', label: 'Physical therapy' },
  { id: 'transportation', label: 'Medical travel and mileage' },
  { id: 'lodging_medical', label: 'Lodging for medical care' },
  { id: 'hearing', label: 'Hearing' },
  { id: 'fertility', label: 'Fertility treatment' },
  { id: 'long_term_care_services', label: 'Long-term care services' },
  { id: 'first_aid', label: 'First aid supplies' },
  { id: 'supplements', label: 'Nutritional supplements' },
  { id: 'massage', label: 'Massage therapy' },
  { id: 'weight_loss', label: 'Weight-loss program' },
  { id: 'exercise_equipment', label: 'Exercise equipment' },
  { id: 'special_foods', label: 'Special foods' },
  { id: 'home_modification', label: 'Home modification' },
  { id: 'cosmetic', label: 'Cosmetic procedure' },
  { id: 'teeth_whitening', label: 'Teeth whitening' },
  { id: 'general_wellness', label: 'General wellness / toiletries' },
  { id: 'gym_membership', label: 'Gym / fitness membership' },
  { id: 'insurance_premium', label: 'Insurance premium' },
  { id: 'other_non_qualified', label: 'Other — not qualified' },
  { id: 'uncategorized', label: 'Not sure' },
];

export function categoryLabel(id: string | null | undefined): string {
  if (!id) return '—';
  return CATEGORIES.find((c) => c.id === id)?.label ?? id.replace(/_/g, ' ');
}

/** Document types a person can attach. `eob` covers any insurance statement. */
export const DOC_TYPES: { id: string; label: string }[] = [
  { id: 'receipt', label: 'Receipt or statement' },
  { id: 'eob', label: 'Insurance statement (EOB)' },
  { id: 'lmn', label: 'Doctor’s note' },
  { id: 'rx', label: 'Prescription' },
  { id: 'order_invoice', label: 'Order invoice' },
];

export function docLabel(type: string): string {
  return DOC_TYPES.find((t) => t.id === type)?.label ?? type.replace(/_/g, ' ');
}

export function sourceIcon(source: string): string {
  if (source === 'amazon_import') return 'cart';
  if (source === 'mileage') return 'car';
  return 'receipt';
}

/**
 * What to call an expense in a list. An Amazon line's merchant is always
 * "Amazon.com" plus an order number, which says nothing; the product does.
 */
export function expenseTitle(e: Pick<Expense, 'source' | 'provider_name' | 'service_description'>): string {
  if (e.source === 'amazon_import' && e.service_description) return e.service_description;
  return e.provider_name ?? 'Unread receipt';
}

export function sourceLabel(source: string): string {
  if (source === 'amazon_import') return 'Amazon order';
  if (source === 'mileage') return 'Mileage';
  return 'Receipt';
}

export interface Attention {
  tone: 'stop' | 'warn';
  label: string;
}

/**
 * Why a waiting record needs a look, in a few words, for list rows.
 *
 * Derived from the record's own fields rather than its stored verdict: the
 * verdict is written when the receipt is read and goes stale the moment a
 * person corrects a date.
 */
export function attention(e: Expense, establishmentDate: string | null): Attention | null {
  if (e.status !== 'draft') return null;
  if (e.date_of_service && establishmentDate && e.date_of_service < establishmentDate) {
    return { tone: 'stop', label: 'Before the HSA opened' };
  }
  const resolved = (e.qual_override_reason ?? '').trim().length >= 10;
  if (e.qual_verdict === 'dual_purpose' && !resolved) {
    const hasNote = e.artifacts.some((a) => a.type === 'lmn' || a.type === 'rx');
    if (!hasNote) return { tone: 'warn', label: 'Doctor’s note or reason needed' };
  }
  if (e.qual_verdict === 'advisory_flag' && !resolved) {
    return { tone: 'warn', label: 'May not qualify' };
  }
  // A missing person is not a problem to report: choosing who it was for is
  // the one tap every review includes.
  if (!e.date_of_service || e.amount_claimed_cents === null) {
    return { tone: 'warn', label: 'Needs details' };
  }
  if ((e.review_confidence ?? 1) < 0.75) return { tone: 'warn', label: 'Check the details' };
  return null;
}

/** Stop first, then warnings, then everything else oldest first. */
export function byPriority(establishmentDate: string | null) {
  const rank = (e: Expense): number => {
    const a = attention(e, establishmentDate);
    return a === null ? 2 : a.tone === 'stop' ? 0 : 1;
  };
  return (a: Expense, b: Expense): number =>
    rank(a) - rank(b) ||
    (a.date_of_service ?? '9999').localeCompare(b.date_of_service ?? '9999');
}
